import type { LucideIcon } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { MoneyValue } from './MoneyValue';
import { number } from '@/lib/format';
import { cn } from '@/lib/utils';

interface ShiftSummaryCardProps {
  label: string;
  icon: LucideIcon;
  count: number;
  gross: number;
  discount?: number;
  accent?: 'crimson' | 'gold' | 'neutral';
}

export function ShiftSummaryCard({
  label,
  icon: Icon,
  count,
  gross,
  discount,
  accent = 'neutral',
}: ShiftSummaryCardProps) {
  return (
    <Card className="overflow-hidden">
      <div
        className={cn(
          'flex flex-wrap items-center gap-x-2 gap-y-1 px-5 py-3',
          accent === 'crimson' && 'bg-primary text-primary-foreground',
          accent === 'gold' && 'bg-gold-500 text-ink-900',
          accent === 'neutral' && 'bg-secondary text-ink-900',
        )}
      >
        {/* WHY THE HEADER WRAPS. On the shift summary these tiles sit in a
            `max-w-4xl` page at `xl:grid-cols-4`, which makes each tile 200px —
            40px of padding leaves 160px, and the count badge alone takes 82-108
            of it. There is no arrangement in which "Kartu Debit/Kredit (EDC
            Permata)" and "1.432 transaksi" share one line at that width, so the
            badge is allowed to wrap onto its own line instead of being painted
            over the label (it was: every tender overlapped at >=1280px).

            Icon and label are one flex item so the badge is the only thing that
            wraps — wrapping them individually would strand the icon alone on
            the first line. The pair keeps `flex-basis: auto` (via `flex-auto`)
            on purpose: a zero basis would make it always "fit", so the badge
            would never wrap and the label would be squeezed to ~40px again.

            `break-words` is not only for the long canonical label. A tender key
            the API does not map renders raw (`shiftTenderRows` -> `tenderLabel`
            falls back to the key), and those keys are arbitrary — an unbroken
            one like `pembayaranviagerbangdigitalnusantara` ran clean out of the
            card at EVERY width, 1024px included, until it could break. The
            label wraps rather than truncates because a cashier reconciling the
            drawer has to tell an EDC tender from an e-money one. */}
        <div className="flex min-w-0 flex-auto items-center gap-2">
          <Icon className="h-5 w-5 shrink-0" />
          <span className="min-w-0 break-words font-display font-semibold leading-tight">
            {label}
          </span>
        </div>
        <span className="tnum ml-auto shrink-0 rounded-full bg-black/10 px-2.5 py-0.5 text-xs font-bold">
          {number(count)} transaksi
        </span>
      </div>
      <div className="px-5 py-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Bruto</p>
        <MoneyValue value={gross} emphasis className="text-2xl" />
        {typeof discount === 'number' && discount > 0 && (
          <p className="mt-1 text-xs text-success">
            Diskon <MoneyValue value={discount} className="text-xs text-success" />
          </p>
        )}
      </div>
    </Card>
  );
}
